/**
 * Light/dark theme helpers: persist preference and apply data-theme on <html>.
 * Uses plain localStorage (not store2) so public/index.html can read the same key before React loads.
 */
export const THEME_STORAGE_KEY = 'eio-theme';
export const THEME_LIGHT = 'light';
export const THEME_DARK = 'dark';

/**
 * Shared react-select styles using CSS variables so light/dark switch without re-coding every page.
 * Pages can merge extras via mergeSelectStyles({ container: ... }).
 */
export const selectStyles = {
	menu: (provided) => ({
		...provided,
		zIndex: 99999,
		backgroundColor: 'var(--eio-bg-elevated)',
		color: 'var(--eio-text)',
	}),
	menuList: (provided) => ({
		...provided,
		backgroundColor: 'var(--eio-bg-elevated)',
	}),
	option: (provided, state) => ({
		...provided,
		backgroundColor: state.isSelected
			? 'var(--eio-primary)'
			: state.isFocused
				? 'var(--eio-bg-muted)'
				: 'transparent',
		color: state.isSelected ? '#fff' : 'var(--eio-text)',
		cursor: 'pointer',
	}),
	control: (provided, state) => ({
		...provided,
		margin: 0,
		marginBottom: 0,
		backgroundColor: 'var(--eio-input-bg)',
		borderColor: state.isFocused ? 'var(--eio-primary)' : 'var(--eio-border)',
		boxShadow: state.isFocused ? '0 0 0 3px rgba(91, 163, 217, 0.2)' : 'none',
		color: 'var(--eio-text)',
		'&:hover': {
			borderColor: 'var(--eio-primary)',
		},
	}),
	singleValue: (provided) => ({
		...provided,
		color: 'var(--eio-text)',
	}),
	multiValue: (provided) => ({
		...provided,
		backgroundColor: 'var(--eio-bg-muted)',
	}),
	multiValueLabel: (provided) => ({
		...provided,
		color: 'var(--eio-text)',
	}),
	multiValueRemove: (provided) => ({
		...provided,
		color: 'var(--eio-text-muted)',
		':hover': {
			backgroundColor: 'var(--eio-border)',
			color: 'var(--eio-text)',
		},
	}),
	input: (provided) => ({
		...provided,
		color: 'var(--eio-text)',
	}),
	placeholder: (provided) => ({
		...provided,
		color: 'var(--eio-text-muted)',
	}),
	indicatorSeparator: (provided) => ({
		...provided,
		backgroundColor: 'var(--eio-border)',
	}),
	dropdownIndicator: (provided) => ({
		...provided,
		color: 'var(--eio-text-muted)',
		':hover': {
			color: 'var(--eio-text)',
		},
	}),
	clearIndicator: (provided) => ({
		...provided,
		color: 'var(--eio-text-muted)',
		':hover': {
			color: 'var(--eio-text)',
		},
	}),
	container: (provided) => ({
		...provided,
		width: 'auto',
		minWidth: '250px',
	}),
};

/**
 * Merge page-specific react-select style overrides on top of the shared theme styles.
 * Page overrides receive the already-themed provided object.
 */
export function mergeSelectStyles(overrides = {}) {
	const merged = { ...selectStyles };
	Object.keys(overrides).forEach((key) => {
		const baseFn = selectStyles[key];
		const overrideFn = overrides[key];
		if (typeof overrideFn !== 'function') {
			merged[key] = overrideFn;
			return;
		}
		merged[key] = (provided, state) => {
			const fromBase = typeof baseFn === 'function' ? baseFn(provided, state) : provided;
			return overrideFn(fromBase, state);
		};
	});
	return merged;
}

/**
 * Read the saved theme; defaults to light when missing or invalid.
 */
export function getStoredTheme() {
	try {
		const stored = localStorage.getItem(THEME_STORAGE_KEY);
		return stored === THEME_DARK ? THEME_DARK : THEME_LIGHT;
	} catch (e) {
		return THEME_LIGHT;
	}
}

/**
 * Apply theme to the document and persist it.
 * @param {'light'|'dark'} theme
 * @returns {'light'|'dark'}
 */
export function applyTheme(theme) {
	const next = theme === THEME_DARK ? THEME_DARK : THEME_LIGHT;
	document.documentElement.setAttribute('data-theme', next);
	try {
		localStorage.setItem(THEME_STORAGE_KEY, next);
	} catch (e) {
		// Ignore quota / private-mode failures; in-memory attribute still applies.
	}
	return next;
}

/**
 * Flip between light and dark.
 */
export function toggleTheme() {
	return applyTheme(getStoredTheme() === THEME_DARK ? THEME_LIGHT : THEME_DARK);
}

/**
 * Re-apply whatever is stored (call once at app boot).
 */
export function applyStoredTheme() {
	return applyTheme(getStoredTheme());
}
